import {
  Component,
  type ErrorInfo,
  type GetDerivedStateFromError,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

export interface ErrorBoundaryFallbackProps {
  error: unknown;
  resetErrorBoundary: () => void;
}

export type ErrorBoundaryFallbackRender = (props: ErrorBoundaryFallbackProps) => ReactNode;

export interface ErrorBoundaryProps extends PropsWithChildren {
  fallback?: ReactNode;
  fallbackRender?: ErrorBoundaryFallbackRender;
  onError?: (error: unknown, info: ErrorInfo) => void;
  onReset?: () => void;
  resetKeys?: readonly unknown[];
}

interface ErrorBoundaryState {
  error: unknown;
  hasError: boolean;
}

const hasResetKeysChanged = (
  previous: readonly unknown[] = [],
  next: readonly unknown[] = []
): boolean =>
  previous.length !== next.length || previous.some((item, index) => !Object.is(item, next[index]));

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    error: undefined,
    hasError: false,
  };

  static getDerivedStateFromError: GetDerivedStateFromError<
    ErrorBoundaryProps,
    ErrorBoundaryState
  > = (error) => ({ error, hasError: true });

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  componentDidUpdate(previousProps: ErrorBoundaryProps): void {
    if (this.state.hasError && hasResetKeysChanged(previousProps.resetKeys, this.props.resetKeys)) {
      this.resetErrorBoundary();
    }
  }

  private resetErrorBoundary = (): void => {
    this.props.onReset?.();
    this.setState({ error: undefined, hasError: false });
  };

  render() {
    const {
      state: { error, hasError },
      props: { fallback, fallbackRender, children },
    } = this;

    if (!hasError) return children;
    if (fallbackRender) {
      return fallbackRender({ error, resetErrorBoundary: this.resetErrorBoundary });
    }
    return fallback ?? null;
  }
}
